import React, { useState, useEffect, useRef, useContext } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL, getBytes } from 'firebase/storage';
import Compressor from 'compressorjs';
import { pdf } from '@react-pdf/renderer';
import { db } from '../Firebase Functions/firebase';
import { AuthContext } from '../hooks/AuthProvider';
import IncidentReportPDF from '../PDF Functions/IncidentReportPDF';
import '../Services Logic/Job List/jobliststyles.css';

function IncidentReportList() {
    const { currentUser: user, loading: authLoading } = useContext(AuthContext);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [modalImage, setModalImage] = useState(null);
    const closingRef = useRef(false);

    useEffect(() => {
        const fetchReports = async () => {
            try {
                const snapshot = await getDocs(collection(db, 'incidentReports'));
                const storage = getStorage();
                const fetchedReports = [];

                for (const docSnap of snapshot.docs) {
                    const reportData = docSnap.data();
                    const imageUrls = {};

                    if (Array.isArray(reportData.attachments?.imageURLs)) {
                        for (let i = 0; i < reportData.attachments.imageURLs.length; i++) {
                            const url = reportData.attachments.imageURLs[i];
                            try {
                                const path = decodeURIComponent(url.split('/o/')[1].split('?')[0].replace(/%2F/g, '/'));
                                const imgRef = ref(storage, path);
                                const downloadURL = await getDownloadURL(imgRef);
                                imageUrls[`image${i}`] = downloadURL;
                            } catch (e) {
                                console.warn('Failed to resolve image URL:', url);
                            }
                        }
                    }

                    fetchedReports.push({
                        id: docSnap.id,
                        ...reportData,
                        imageUrls,
                    });
                }

                setReports(fetchedReports);
            } catch (err) {
                console.error('Error fetching incident reports:', err);
            } finally {
                setLoading(false);
            }
        };

        if (!authLoading) fetchReports();
    }, [authLoading]);

    const openImageModal = (url) => {
        if (closingRef.current) return;
        setModalImage(url);
    };

    const closeImageModal = () => {
        closingRef.current = true;
        setModalImage(null);
        setTimeout(() => (closingRef.current = false), 200);
    };

    const handleModalOverlayClick = (e) => {
        if (!e.target.closest('.modal-content')) closeImageModal();
    };
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: 0.4,
      maxWidth: 1024,
      maxHeight: 1024,
      convertSize: 200000,
      success: resolve,
      error: reject,
    });
  });
};

const convertImagesToBase64 = async (imageUrls) => {
  const base64Images = [];
  const storage = getStorage();

  for (const url of imageUrls.filter(Boolean)) {
    try {
      const match = url.match(/o\/(.+?)\?/);
      if (!match) continue;

      const path = decodeURIComponent(match[1]);
      const fileRef = ref(storage, path);
      const bytes = await getBytes(fileRef);
      const compressed = await compressImage(new Blob([bytes], { type: 'image/jpeg' }));

      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(compressed);
      });

      base64Images.push(base64);
    } catch (error) {
      console.error('Error converting image to base64:', error);
    }
  }

  return base64Images;
};

const handleDownloadPDF = async (reportData) => {
  try {
    const imageUrls = Object.values(reportData.imageUrls || {});
    const base64Images = await convertImagesToBase64(imageUrls);

    const pdfBlob = await pdf(
      <IncidentReportPDF reportData={reportData} base64Images={base64Images} />
    ).toBlob();

    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `IncidentReport_${reportData.id || 'report'}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error generating PDF:', err);
    alert('Failed to generate PDF.');
  }
};


    const filteredReports = reports.filter((report) => {
        const query = searchQuery.toLowerCase();
        return (
            report.createdBy?.toLowerCase().includes(query) ||
            report.status?.toLowerCase().includes(query) ||
            report.occurrence?.location?.toLowerCase().includes(query)
        );
    });

    if (authLoading) return <div>Loading authentication...</div>;
    if (!user) return <div>Please log in to view incident reports</div>;
    if (loading) return <div>Loading incident reports...</div>;

    return (
        <div className="job-list-container">
            <div className="search-container">
                <input
                    type="text"
                    placeholder="Search by creator, status, or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                />
            </div>
            <div className="job-list-header">
                <h1>Incident Reports</h1>
                <p>Total Reports: <strong>{filteredReports.length}</strong></p>
            </div>
            <div className="job-grid">
                {filteredReports.map((report) => (
                    <div key={report.id} className="job-card">
                        <div className="job-header">
                            <h3>Status: {report.status || 'Unknown'}</h3>
                            <p>Created by: {report.createdBy}</p>
                            <p>Date: {new Date(report.timestamp).toLocaleDateString()}</p>
                        </div>

                        {report.imageUrls && Object.values(report.imageUrls).length > 0 && (
                            <div className="image-grid">
                                {Object.entries(report.imageUrls).map(([key, url]) => (
                                    <div key={key} className="image-container">
                                        <img
                                            src={url}
                                            alt={`Incident attachment ${key}`}
                                            onClick={() => openImageModal(url)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="notes-section">
                            <h4>Location:</h4>
                            <p>{report.occurrence?.location || 'N/A'}</p>
                            <h4>Explanation:</h4>
                            <p>{report.correctiveAction?.explanation || 'No explanation provided'}</p>
                        </div>

                        <div className="button-container">
                            <button onClick={() => handleDownloadPDF(report)} className="email-button">
                                Download PDF
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {modalImage && (
                <div className="modal-overlay" onClick={handleModalOverlayClick}>
                    <div className="modal-content">
                        <button type="button" className="close-modal" onClick={(e) => { e.stopPropagation(); closeImageModal(); }}>
                            ×
                        </button>
                        <img
                            src={modalImage}
                            alt="Enlarged"
                            onClick={(e) => e.stopPropagation()}
                            style={{ pointerEvents: 'none' }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default IncidentReportList;
